var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useId } from '@floating-ui/react';
import { ResizeObserver } from '@juggle/resize-observer';
import Anchor from '@telia-front/anchor';
import Badge from '@telia-front/badge';
import Button from '@telia-front/button';
import Card, { CardContent, CardTop } from '@telia-front/card';
import { ClickArea, ClickAreaTrigger } from '@telia-front/click-area';
import { EqualHeight } from '@telia-front/equal-height';
import FormHelper from '@telia-front/form-helper';
import Grid, { GridColumn } from '@telia-front/grid';
import Heading from '@telia-front/heading';
import Icon from '@telia-front/icon';
import Image from '@telia-front/image';
import { InputContext } from '@telia-front/input-provider';
import List, { ListItem } from '@telia-front/list';
import ObjectFit from '@telia-front/object-fit';
import Select from '@telia-front/select';
import Selector from '@telia-front/selector';
import Tag from '@telia-front/tag';
import TagGroup from '@telia-front/tag-group';
import Tooltip from '@telia-front/tooltip';
import TooltipToggle from '@telia-front/tooltip-toggle';
const ProductCard = React.forwardRef(function ProductCard(props, ref) {
    const { input } = React.useContext(InputContext);
    const id = useId();
    return React.createElement(ProductCardInner, Object.assign({}, props, { input: input, id: id, linkRef: ref }));
});
class ProductCardInner extends React.Component {
    constructor(props) {
        super(props);
        this.figureRef = null;
        this.resizeObserver = null;
        this.handleResize = () => {
            const element = this.elementRef.current;
            if (element) {
                const elementWidth = element.offsetWidth;
                if (elementWidth !== this.state.elementWidth) {
                    this.setState({ elementWidth });
                }
            }
        };
        this.handleCompareMouseEnter = () => {
            if (this.state.hover !== 'compare') {
                this.resetProperties();
                this.setFigureStyleProperty('transform', 'translateX(-60px)');
                this.setState({
                    hover: 'compare',
                });
            }
        };
        this.handleCompareMouseLeave = () => {
            if (this.state.hover === 'compare') {
                this.resetProperties();
                this.setState({
                    hover: null,
                });
            }
        };
        this.handleColorMouseEnter = (value) => {
            if (this.state.color !== value) {
                this.setState({
                    color: value,
                });
            }
        };
        this.handleColorMouseLeave = (value) => {
            if (this.state.color === value) {
                this.setState({
                    color: null,
                });
            }
        };
        this.handleColorMouseClick = (value) => {
            this.setState({
                color: value,
            });
        };
        this.elementRef = React.createRef();
        this.state = {
            color: null,
            figureStyle: {},
            hover: null,
            memory: null,
            elementWidth: 0,
        };
    }
    componentDidMount() {
        this.handleResize();
        this.resizeObserver = new ResizeObserver(() => {
            this.handleResize();
        });
        if (this.elementRef.current) {
            this.resizeObserver.observe(this.elementRef.current);
        }
    }
    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
    resetProperties() {
        // Scale figure to its original size
        this.setFigureStyleProperty('transform', 'translateX(0)');
    }
    setFigureStyleProperty(property, value) {
        this.setState(prevState => {
            return {
                figureStyle: Object.assign(Object.assign({}, prevState.figureStyle), { [property]: value }),
            };
        });
    }
    getImageProps() {
        const { color } = this.state;
        const { colors } = this.props;
        if (colors && Array.isArray(colors)) {
            if (color) {
                const currentColor = colors.find(c => c.label === color);
                if (currentColor && currentColor.image) {
                    return currentColor.image;
                }
            }
            const currentColor = colors.find(c => c.isCurrent);
            if (currentColor && currentColor.image) {
                return currentColor.image;
            }
        }
        return this.props.image;
    }
    getCurrentColor() {
        const { color } = this.state;
        const { colors } = this.props;
        if (colors && Array.isArray(colors)) {
            if (color) {
                return colors.find(c => c.label === color);
            }
            return colors.find(c => c.isCurrent);
        }
        return undefined;
    }
    getCurrentColorLabel() {
        var _a;
        return ((_a = this.getCurrentColor()) === null || _a === void 0 ? void 0 : _a.label) || '';
    }
    BEM() {
        const classArray = ['product-card'];
        if (this.state.hover) {
            classArray.push('product-card--hover-' + this.state.hover);
        }
        if (this.props.inactive) {
            classArray.push('product-card--inactive');
        }
        if (this.props.type) {
            classArray.push(`product-card--type-${this.props.type}`);
        }
        if (this.props.className) {
            classArray.push(this.props.className);
        }
        return classArray.join(' ');
    }
    footerBEM() {
        const FOOTER_MIN_WIDTH = 260;
        const classArray = ['product-card__footer'];
        if (this.state.elementWidth <= FOOTER_MIN_WIDTH) {
            classArray.push('product-card__footer--narrow');
        }
        return classArray.join(' ');
    }
    getTags(tags, index) {
        var _a;
        if (!Array.isArray(tags)) {
            return this.renderTags(tags, `${(_a = tags.tooltip) === null || _a === void 0 ? void 0 : _a.id}--${index}`);
        }
        else {
            return this.renderTags(tags);
        }
    }
    renderTags(tags, id) {
        let content;
        const responsiveProps = [
            {
                breakPoint: 'xs',
                props: {
                    size: 'small',
                    fontSize: 'small',
                },
            },
            {
                breakPoint: 'sm',
                props: {
                    size: 'medium',
                    fontSize: 'medium',
                },
            },
            {
                breakPoint: 'md',
                props: {
                    size: 'large',
                    fontSize: 'large',
                },
            },
        ];
        if (Array.isArray(tags)) {
            const isCustomResponsive = tags.some(tag => tag.responsive);
            const modifiedTags = tags.map((tag) => {
                if (!this.props.tagGroupSize && !isCustomResponsive) {
                    return Object.assign(Object.assign({}, tag), { responsive: responsiveProps });
                }
                return Object.assign({}, tag);
            });
            content = React.createElement(TagGroup, { tags: modifiedTags, size: this.props.tagGroupSize });
        }
        else {
            let tagProps = Object.assign({ className: 'product-card__tag' }, tags);
            if (!tags.size && !tags.responsive) {
                tagProps = Object.assign({ responsive: responsiveProps }, tagProps);
            }
            if (tags.tooltip && id) {
                content = React.createElement(Tag, Object.assign({}, tagProps, { tooltip: { id, children: tags.tooltip.children } }));
            }
            else {
                content = React.createElement(Tag, Object.assign({}, tagProps));
            }
        }
        return React.createElement("div", { className: "product-card__tags" }, content);
    }
    getUrl() {
        var _a;
        const { url: productUrl } = this.props;
        const currentColor = this.getCurrentColor();
        const currentMemory = this.getCurrentMemory();
        if (currentColor && currentMemory && Array.isArray(currentColor.url)) {
            return ((_a = currentColor.url.find(url => url.memory === currentMemory.value)) === null || _a === void 0 ? void 0 : _a.value) || productUrl;
        }
        return (!Array.isArray(currentColor === null || currentColor === void 0 ? void 0 : currentColor.url) && (currentColor === null || currentColor === void 0 ? void 0 : currentColor.url)) || (currentMemory === null || currentMemory === void 0 ? void 0 : currentMemory.url) || productUrl;
    }
    getCurrentMemory() {
        var _a;
        const { memories } = this.props;
        if (!((_a = memories === null || memories === void 0 ? void 0 : memories.items) === null || _a === void 0 ? void 0 : _a.length)) {
            return undefined;
        }
        if (this.state.memory) {
            return memories.items.find(memory => memory.value === this.state.memory);
        }
        return memories.items.find(memory => memory.isCurrent);
    }
    getPrice(type) {
        const price = this.props[type];
        const currentMemory = this.getCurrentMemory();
        return currentMemory ? currentMemory[type] : price;
    }
    renderMonthlyPrice(monthlyPrice) {
        return (React.createElement("div", { className: "product-card__monthly-price-inner" },
            React.createElement("p", { className: "text-tiny text-primary" }, monthlyPrice.label),
            React.createElement("p", { className: "product-card__price" }, this.props.tax ? (React.createElement(React.Fragment, null,
                React.createElement("span", { className: "product-card__price-text" }, this.getPriceAsString(monthlyPrice.price)),
                React.createElement("span", null, " "),
                React.createElement("span", { className: "product-card__tax-text" }, this.props.tax))) : (React.createElement(React.Fragment, null, this.getPriceAsString(monthlyPrice.price)))),
            monthlyPrice.tagText && (React.createElement(React.Fragment, null,
                React.createElement(Tag, { size: "tiny", text: monthlyPrice.tagText, purpose: monthlyPrice.tagPurpose, isDisabled: this.props.inactive }),
                ' ')),
            React.createElement("span", { className: "text-tiny text-light product-card__price-months" }, monthlyPrice.length)));
    }
    getPriceAsString(price) {
        if (typeof price === 'string') {
            return price;
        }
        return `${price.value} ${price.unit}`;
    }
    renderPriceAsValue(price) {
        if (typeof price === 'string') {
            return React.createElement(React.Fragment, null, this.getPriceAsString(price));
        }
        return (React.createElement(React.Fragment, null,
            price.value,
            ' ' + price.unit));
    }
    renderPrice() {
        const price = this.getPrice('price');
        const previousPrice = this.getPrice('previousPrice');
        if (price && previousPrice) {
            return (React.createElement(React.Fragment, null,
                React.createElement("p", { className: "text-tiny text-primary" }, price.label),
                React.createElement("p", { className: "product-card__discount-price" }, this.props.tax ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "product-card__price-text" }, this.renderPriceAsValue(price.price)),
                    React.createElement("span", null, " "),
                    React.createElement("span", { className: "product-card__tax-text" }, this.props.tax))) : (React.createElement(React.Fragment, null, this.renderPriceAsValue(price.price)))),
                price.priceEndDate && price.priceEndDateLabel && (React.createElement("p", { className: "text-tiny text-light" },
                    price.priceEndDateLabel + ' ',
                    " ",
                    React.createElement("time", { dateTime: price.priceEndDate.isoDate }, price.priceEndDate.formattedDate))),
                React.createElement("p", { className: "text-tiny text-light" },
                    previousPrice.label + ' ',
                    previousPrice.ignoreCrossOut ? (React.createElement("strong", null,
                        this.getPriceAsString(previousPrice.price),
                        this.props.tax && React.createElement(React.Fragment, null, ` ${this.props.tax}`))) : (React.createElement("del", null,
                        React.createElement("strong", null,
                            this.getPriceAsString(previousPrice.price),
                            this.props.tax && React.createElement(React.Fragment, null, ` ${this.props.tax}`)))))));
        }
        else if (price) {
            return (React.createElement("div", { className: "product-card__price-inner" },
                React.createElement("p", { className: "text-tiny text-primary" }, price.label),
                React.createElement("p", { className: "product-card__current-price" }, this.props.tax ? (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "product-card__price-text" }, this.renderPriceAsValue(price.price)),
                    React.createElement("span", null, " "),
                    React.createElement("span", { className: "product-card__tax-text" }, this.props.tax))) : (React.createElement(React.Fragment, null, this.renderPriceAsValue(price.price))))));
        }
        return null;
    }
    renderMemories(memories) {
        const { items } = memories, rest = __rest(memories, ["items"]);
        const currentMemory = this.getCurrentMemory();
        return (React.createElement(Selector, Object.assign({}, rest, { items: memories.items.map(memory => ({
                id: memory.id,
                value: memory.value,
                name: memory.id,
                label: memory.label,
                ariaLabel: memory.ariaLabel,
                onChange: memory.onClick,
            })), align: "left", value: currentMemory === null || currentMemory === void 0 ? void 0 : currentMemory.value, onChange: (value) => this.setState({ memory: value }), hideLabel: true, size: "small", className: "product-card__memory-selector" })));
    }
    renderColorSelect(colors) {
        return (React.createElement(Select, Object.assign({}, colors, { className: "product-card__color-select", size: "small", hideLabel: "no-break", portalTarget: null, simpleValue: true, options: colors.options.map(option => {
                const { color } = option, rest = __rest(option, ["color"]);
                return Object.assign(Object.assign({}, rest), { children: (React.createElement(Grid, { noWrap: true, gutter: "small", verticalGutter: "none", align: ['middle-xs'] },
                        React.createElement(GridColumn, { width: ['min'] },
                            React.createElement("div", { className: "product-card__color-inner", style: {
                                    background: color,
                                } })),
                        React.createElement(GridColumn, { width: ['max'] }, rest.label))) });
            }) })));
    }
    renderColors(colors) {
        const { input } = this.props;
        const { color: currentColor } = this.state;
        if (!Array.isArray(colors)) {
            return this.renderColorSelect(colors);
        }
        const label = this.getCurrentColorLabel();
        return (React.createElement("div", { className: "product-card__colors" },
            colors.map((color, index) => {
                const colorStyle = {
                    background: color.color,
                };
                const mouseEnterHandler = () => {
                    if (color.label && color.image) {
                        this.handleColorMouseEnter(color.label);
                    }
                };
                const mouseLeaveHandler = () => {
                    if (color.label && color.image) {
                        this.handleColorMouseLeave(color.label);
                    }
                };
                const mouseClickHandler = () => {
                    if (color.label && color.image) {
                        this.handleColorMouseClick(color.label);
                    }
                };
                const BEM = () => {
                    const classArray = ['product-card__color'];
                    if ((currentColor && currentColor === color.label) || (!currentColor && color.isCurrent)) {
                        classArray.push('is-current');
                    }
                    return classArray.join(' ');
                };
                if (color.url) {
                    return (React.createElement(Anchor, { key: index, className: BEM(), "aria-label": color.ariaLabel, url: this.getUrl(), onClick: mouseClickHandler || color.onClick, tabIndex: -1, attributes: {
                            onMouseEnter: this.isMouseInput(input) ? mouseEnterHandler : undefined,
                            onMouseLeave: this.isMouseInput(input) ? mouseLeaveHandler : undefined,
                        } },
                        React.createElement("div", { className: "product-card__color-inner", style: colorStyle }),
                        React.createElement("span", { className: "sr-only" }, color.label)));
                }
                return (React.createElement("div", { key: index, className: BEM(), onMouseEnter: this.isMouseInput(input) ? mouseEnterHandler : undefined, onMouseLeave: this.isMouseInput(input) ? mouseLeaveHandler : undefined },
                    React.createElement("div", { className: "product-card__color-inner", style: colorStyle }),
                    React.createElement("span", { className: "sr-only" }, color.label)));
            }),
            label && React.createElement("div", { className: "product-card__color-label" }, label)));
    }
    get titleId() {
        return `${this.props.id}-title`;
    }
    renderTitle(title) {
        const { headingLevel = 1 } = this.props;
        return (React.createElement(Heading, { level: headingLevel, className: "h4 text-bold", id: this.titleId },
            React.createElement(ClickAreaTrigger, null,
                React.createElement(Anchor, { url: this.getUrl(), onClick: this.props.onClick, isTextColor: true, isUnderlined: false, ref: this.props.linkRef },
                    title.brand && React.createElement(React.Fragment, null,
                        title.brand,
                        " "),
                    title.name,
                    title.info && React.createElement(React.Fragment, null,
                        " ",
                        title.info)))));
    }
    renderFooter() {
        var _a;
        const monthlyPrice = this.getPrice('monthlyPrice');
        return (React.createElement("div", { className: this.footerBEM() },
            React.createElement("div", { className: "product-card__footer--column-inner" },
                React.createElement("div", { className: "product-card__footer--column-left" },
                    React.createElement(EqualHeight, { group: "product-card-footer-left" }, monthlyPrice && this.renderMonthlyPrice(monthlyPrice))),
                React.createElement("div", { className: "product-card__footer--column-right" },
                    React.createElement(EqualHeight, { group: "product-card-footer-right" }, this.renderPrice()))),
            React.createElement(EqualHeight, { group: "product-card-footer-button" }, this.props.cartButton && (React.createElement("div", { className: "product-card__cart-button-wrapper" },
                React.createElement(Button, Object.assign({ type: "expressive", iconLeft: "#cart" }, this.props.cartButton)),
                ((_a = this.props.cartButtonHelper) === null || _a === void 0 ? void 0 : _a.visible) && (React.createElement(FormHelper, Object.assign({ type: "valid" }, this.props.cartButtonHelper.options, { className: "product-card__cart-button-helper" }))))))));
    }
    compareButtonBEM(compare) {
        const classArray = ['product-card__compare-button'];
        if (compare.isActive) {
            classArray.push('is-active');
        }
        return classArray.join(' ');
    }
    isMouseInput(input) {
        return input === 'mouse';
    }
    renderCompareButton(compare) {
        const { input } = this.props;
        return (React.createElement("button", { className: this.compareButtonBEM(compare), id: compare.id, type: "button", onClick: e => {
                var _a;
                e.stopPropagation();
                (_a = compare.onClick) === null || _a === void 0 ? void 0 : _a.call(compare, e);
            }, onMouseEnter: input && this.isMouseInput(input) ? this.handleCompareMouseEnter : undefined, onMouseLeave: input && this.isMouseInput(input) ? this.handleCompareMouseLeave : undefined, "aria-labelledby": `${compare.id} ${this.titleId}` },
            React.createElement("span", { className: "product-card__compare-button-inner" },
                React.createElement(Icon, { modifiers: "#compare", className: "product-card__compare-button-icon" }),
                React.createElement(Icon, { modifiers: "#plus", className: "product-card__compare-button-icon product-card__compare-button-icon--add" }),
                React.createElement(Icon, { modifiers: "#minus", className: "product-card__compare-button-icon product-card__compare-button-icon--remove" }),
                compare.isActive && React.createElement(Badge, { icon: "#check", purpose: "success", size: "small", className: "product-card__compare-button-active-icon" }),
                React.createElement("span", { className: "product-card__compare-button-text" }, compare.text))));
    }
    renderInactiveContent() {
        const { inactiveContent } = this.props;
        return React.createElement("div", { className: "product-card__inactive-content h4" }, inactiveContent);
    }
    renderContent() {
        var _a, _b;
        const figureRef = (el) => (this.figureRef = el);
        return (React.createElement(React.Fragment, null,
            this.props.compare && this.renderCompareButton(this.props.compare),
            this.props.tags && this.getTags(this.props.tags, 'first'),
            React.createElement(CardContent, { className: "product-card__content", elementRef: this.elementRef, responsive: [
                    {
                        breakPoint: 'xs',
                        props: { padding: 'small' },
                    },
                    {
                        breakPoint: 'md',
                        props: {
                            padding: {
                                top: 'medium',
                                bottom: 'small',
                                left: 'medium',
                                right: 'medium',
                            },
                        },
                    },
                ] },
                React.createElement(CardTop, null,
                    React.createElement("figure", { className: "product-card__figure", style: this.state.figureStyle, ref: figureRef },
                        React.createElement(ObjectFit, { fit: "contain", positionX: "center", positionY: "center", className: "product-card__image" },
                            React.createElement(Image, Object.assign({}, this.getImageProps(), { isInactive: this.props.inactive }))),
                        React.createElement(Icon, { className: "product-card__image-compare-icon", modifiers: "#compare" }),
                        React.createElement(ObjectFit, { fit: "contain", positionX: "center", positionY: "center", className: "product-card__image product-card__image--copy" },
                            React.createElement(Image, Object.assign({}, this.getImageProps(), { isInactive: this.props.inactive }))),
                        this.props.inactive && this.renderInactiveContent()),
                    React.createElement("div", { className: "product-card__info" },
                        React.createElement("div", { className: "product-card__info-inner" },
                            React.createElement(EqualHeight, { group: "product-card-colors" }, this.props.colors && this.renderColors(this.props.colors)),
                            React.createElement(EqualHeight, { group: "product-card-title" }, this.renderTitle(this.props.title)),
                            React.createElement(EqualHeight, { group: "product-card-memories" }, ((_b = (_a = this.props.memories) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) && this.renderMemories(this.props.memories)),
                            React.createElement(EqualHeight, { group: "product-card-features" }, this.props.features && this.renderFeatures(this.props.features)),
                            (this.props.price || this.props.monthlyPrice || this.props.cartButton) && this.renderFooter()))))));
    }
    renderFeatures(features) {
        return (React.createElement(List, { className: "product-card__features", type: features.some(feature => feature.icon) ? 'icon' : 'unstyled', noGutter: "vertical", iconPosition: "middle" }, features.map((feature, index) => {
            const content = (React.createElement(React.Fragment, null,
                React.createElement("span", { className: "text-small" },
                    feature.name,
                    " "),
                feature.info && React.createElement("span", { className: "text-small" }, feature.info)));
            return (React.createElement(ListItem, { key: index, icon: feature.icon, iconClassName: "product-card__feature-icon" }, feature.tooltip ? (React.createElement(React.Fragment, null,
                React.createElement(TooltipToggle, { id: feature.tooltip.id }, content),
                React.createElement(Tooltip, { referenceElement: `#${feature.tooltip.id}`, placement: "top" }, feature.tooltip.children))) : (content)));
        })));
    }
    render() {
        return (React.createElement(ClickArea, null,
            React.createElement(Card, { className: this.BEM(), background: "neutral-bg-level-1", type: "light", element: "article", "aria-labelledby": this.titleId }, this.renderContent())));
    }
}
export { ProductCard as default, };
